import {createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
	EntityType,
	Entry,
	FormData,
	FormSelectOptions,
	PropertyDefinition,
	PropertyDefinitionBelongsTo,
	PropertyDefinitionHas,
	PropertyDefinitionSelectStringOptionEntry,
	Repository,
} from '../types';
import {RootState} from '../store';
import {PROPERTY_DEFINITION_TYPES, REPOSITORY_ENTRY_ENTITY_TYPES_BY_SYSTEM_REPOSITORY_IDS} from '../constants';
import {
	sortEntryByRank,
	getEditFormTemplatePropertiesForEntityType,
	getTemplatePropertiesBySystemRepositoryId, isCustomRepository
} from '../common/util';
import {selectAllEntries} from './contentSlice';

interface FormInitPayload {
	formTemplateProperties: PropertyDefinition[],
	formSelectOptions: FormSelectOptions,
	formData: FormData,
}

export interface FormDataState {
  formTemplateProperties: PropertyDefinition[];
  formSelectOptions: FormSelectOptions;
  formRepository: Repository | null;
  formData: FormData;
  drawerFormTemplateProperties: PropertyDefinition[];
  drawerFormSelectOptions: FormSelectOptions;
  drawerFormData: FormData;
  addFormRepositoryOptions: PropertyDefinitionSelectStringOptionEntry[];
	addFormRepository: Repository | null;
  editFormEntry: Entry | null;
  isEditDialogOpen: boolean;
  confirmDeleteDialogEntry: Entry | null;
  isAddRepositoryDialogOpen: boolean;
  isAddRepositoryPropertyDialogOpen: boolean;
  isEditRepositoryDialogOpen: boolean;
	addRepositoryEntryDialog: AddRepositoryEntryDialogState;
}

export interface OpenAddFormProps {
	repository: Repository;
}

/** Non-null when the add-repository-entry dialog is open. */
export type AddRepositoryEntryDialogState = {
	repositoryId: string;
	linkToProperty?: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
	linkToEntryId?: string;
} | null;

const initialState: FormDataState = {
	formTemplateProperties: [],
	formSelectOptions: {},
	formRepository: null,
	formData: {},
	drawerFormTemplateProperties: [],
	drawerFormSelectOptions: {},
	drawerFormData: {},
	addFormRepositoryOptions: [],
	addFormRepository: null,
	editFormEntry: null,
	isEditDialogOpen: false,
	confirmDeleteDialogEntry: null,
	isAddRepositoryDialogOpen: false,
	isAddRepositoryPropertyDialogOpen: false,
	isEditRepositoryDialogOpen: false,
	addRepositoryEntryDialog: null,
};

const formDataSlice = createSlice({
	name: 'formData',
	initialState,
	reducers: {
		openAddForm(state, action: PayloadAction<OpenAddFormProps>) {
			const {repository} = action.payload;
			state.addFormRepository = repository;
		},
		isAddRepositoryDialogOpenSetState(state, action: PayloadAction<boolean>) {
			state.isAddRepositoryDialogOpen = action.payload;
		},
		isAddRepositoryPropertyDialogOpenSetState(state, action: PayloadAction<boolean>) {
			state.isAddRepositoryPropertyDialogOpen = action.payload;
		},
		isEditRepositoryDialogOpenSetState(state, action: PayloadAction<boolean>) {
			state.isEditRepositoryDialogOpen = action.payload;
		},
		closeAddForm(state) {
			state.addFormRepository = null;
		},
		formInit(state, action: PayloadAction<FormInitPayload>) {
			const {
				formTemplateProperties,
				formSelectOptions,
				formData,
			} = action.payload;
			state.formTemplateProperties = formTemplateProperties;
			state.formSelectOptions = formSelectOptions;
			state.formData = formData;
		},
		formDataSetState(state, action: PayloadAction<FormData>) {
			state.formData = action.payload;
		},
		drawerFormInit(state, action: PayloadAction<FormInitPayload>) {
			const {
				formTemplateProperties,
				formSelectOptions,
				formData,
			} = action.payload;
			state.drawerFormTemplateProperties = formTemplateProperties;
			state.drawerFormSelectOptions = formSelectOptions;
			state.drawerFormData = formData;
		},
		drawerFormDataSetState(state, action: PayloadAction<FormData>) {
			state.drawerFormData = action.payload;
		},
		addFormRepositoryOptionsSetState(state: FormDataState, action: PayloadAction<PropertyDefinitionSelectStringOptionEntry[]>) {
			state.addFormRepositoryOptions = action.payload;
		},
		editFormEntrySetState(state: FormDataState, action: PayloadAction<Entry | null>) {
			state.editFormEntry = action.payload;
		},
		setIsEditDialogOpen(state: FormDataState, action: PayloadAction<boolean>) {
			state.isEditDialogOpen = action.payload;
		},
		formRepositorySetState(state, action: PayloadAction<Repository>) {
			state.formRepository = action.payload;
		},
		confirmDeleteDialogEntrySetState(state: FormDataState, action: PayloadAction<Entry | null>) {
			state.confirmDeleteDialogEntry = action.payload;
		},
		addRepositoryEntryDialogSetState(state: FormDataState, action: PayloadAction<AddRepositoryEntryDialogState>) {
			state.addRepositoryEntryDialog = action.payload;
		},
		resetState() {
			return initialState;
		}
	},
});

export const {
	formDataSetState,
	drawerFormDataSetState,
	formRepositorySetState,
	formInit,
	drawerFormInit,
	addFormRepositoryOptionsSetState,
	openAddForm,
	closeAddForm,
	editFormEntrySetState,
	setIsEditDialogOpen,
	confirmDeleteDialogEntrySetState,
	resetState,
	isAddRepositoryDialogOpenSetState,
	isAddRepositoryPropertyDialogOpenSetState,
	isEditRepositoryDialogOpenSetState,
	addRepositoryEntryDialogSetState,
} = formDataSlice.actions;

export default formDataSlice.reducer;

const selectFormDataState = (state: RootState) => state.formData;

/**
 * generic selectors
 */

export const selectFormData = createSelector([selectFormDataState], (state: FormDataState) => state.formData);
export const selectDrawerFormData = createSelector([selectFormDataState], (state: FormDataState) => state.drawerFormData);
export const selectFormSelectOptions = createSelector([selectFormDataState], (state: FormDataState) => state.formSelectOptions);
export const selectDrawerFormSelectOptions = createSelector([selectFormDataState], (state: FormDataState) => state.drawerFormSelectOptions);
export const selectFormTemplateProperties = createSelector([selectFormDataState], (state: FormDataState) => state.formTemplateProperties);
export const selectDrawerFormTemplateProperties = createSelector([selectFormDataState], (state: FormDataState) => state.drawerFormTemplateProperties);
/**
 * use-case specific selectors
 */

export const selectFormEntityTypeOptions = createSelector([selectFormDataState], (state: FormDataState) => state.addFormRepositoryOptions);

export const selectAddFormRepository = createSelector([selectFormDataState], (state: FormDataState) => state.addFormRepository);
export const selectIsAddDialogOpen = createSelector([selectFormDataState],(state: FormDataState) => Boolean(state.addFormRepository));
export const selectAddFormRepositoryPropertyDefinitions = createSelector([selectAddFormRepository, selectAllEntries], (addFormRepository, allEntries) => {
	if (!addFormRepository) {
		return [];
	} else if (isCustomRepository(addFormRepository.id)) {
		const [firstPropertyDef, ...restDefs] = allEntries
			.filter((entry) => PROPERTY_DEFINITION_TYPES.includes(entry.type) && entry.parentId === addFormRepository.id)
			.sort(sortEntryByRank);
		return (firstPropertyDef ? [{...firstPropertyDef, autoFocus: true}, ...restDefs] : []) as PropertyDefinition[];
	} else {
		return getTemplatePropertiesBySystemRepositoryId(addFormRepository.id);
	}
});
export const selectAddFormRepositoryEntryType = createSelector([selectAddFormRepository], (addFormRepository) => {
	return addFormRepository ? REPOSITORY_ENTRY_ENTITY_TYPES_BY_SYSTEM_REPOSITORY_IDS[addFormRepository.id] : null;
});

export const selectEditFormEntry = createSelector([selectFormDataState], (state: FormDataState) => state.editFormEntry);
export const selectIsEditDialogOpen = createSelector([selectFormDataState], (state: FormDataState) => state.isEditDialogOpen);
export const selectEditFormEntryPropertyDefinitions = createSelector([selectEditFormEntry, selectAllEntries], (editFormEntry, allEntries) => {
	if (!editFormEntry) {
		return [];
	}
	if (editFormEntry.type === EntityType.RepositoryEntry) {
		const [firstPropertyDef, ...restDefs] = allEntries
			.filter((entry) => PROPERTY_DEFINITION_TYPES.includes(entry.type) && entry.parentId === editFormEntry.repositoryId)
			.sort(sortEntryByRank);
		return (firstPropertyDef ? [{...firstPropertyDef, autoFocus: true}, ...restDefs] : []) as PropertyDefinition[];
	} else {
		return getEditFormTemplatePropertiesForEntityType(editFormEntry.type);
	}
});

export const selectConfirmDeleteDialogEntry = createSelector([selectFormDataState], (state: FormDataState) => state.confirmDeleteDialogEntry);
export const selectIsConfirmDeleteDialogOpen = createSelector([selectConfirmDeleteDialogEntry], (confirmDeleteDialogEntry) => Boolean(confirmDeleteDialogEntry));

export const selectIsAddRepositoryDialogOpen = createSelector([selectFormDataState], (state: FormDataState) => state.isAddRepositoryDialogOpen);
export const selectIsAddRepositoryPropertyDialogOpen = createSelector([selectFormDataState], (state: FormDataState) => state.isAddRepositoryPropertyDialogOpen);
export const selectIsEditRepositoryDialogOpen = createSelector([selectFormDataState], (state: FormDataState) => state.isEditRepositoryDialogOpen);

export const selectAddRepositoryEntryDialog = createSelector([selectFormDataState], (state: FormDataState) => state.addRepositoryEntryDialog);
export const selectIsAddRepositoryEntryDialogOpen = createSelector([selectAddRepositoryEntryDialog], (dialog) => Boolean(dialog));

const selectAuthIsAccountDialogOpen = (state: RootState) => state.auth.isAccountDialogOpen;

export const selectIsAnyDialogOpen = createSelector(
	[selectIsAddDialogOpen, selectIsEditDialogOpen, selectConfirmDeleteDialogEntry, selectIsAddRepositoryDialogOpen, selectIsAddRepositoryPropertyDialogOpen, selectIsEditRepositoryDialogOpen, selectIsAddRepositoryEntryDialogOpen, selectAuthIsAccountDialogOpen],
	(isAddDialogOpen, isEditDialogOpen, confirmDeleteDialogEntry, isAddRepositoryDialogOpen, isAddRepositoryPropertyDialogOpen, isEditRepositoryDialogOpen, isAddRepositoryEntryDialogOpen, isAccountDialogOpen) =>
		isAddDialogOpen || isEditDialogOpen || Boolean(confirmDeleteDialogEntry) || isAddRepositoryDialogOpen || isAddRepositoryPropertyDialogOpen || isEditRepositoryDialogOpen || isAddRepositoryEntryDialogOpen || isAccountDialogOpen
);
